import { useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import { Link } from '@mui/joy';

const FAQ_SECTIONS = [
    {
        id: 'history',
        label: 'История проекта',
        content: () => (
            <>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>
                    Проект основан на базе данных «За Христа пострадавшие», созданной Православным Свято-Тихоновским гуманитарным университетом в 1990-е годы. Цель проекта — собрать и систематизировать сведения о судьбах православных христиан, подвергшихся репрессиям в XX веке.
                </Typography>
                <Typography sx={{ color: 'var(--vkui--color_text_primary)' }}>
                    Сегодня в базе десятки тысяч биографических справок, основанных на архивных документах и исследованиях историков.
                </Typography>
            </>
        ),
    },
    {
        id: 'tech',
        label: 'Техническая информация',
        content: () => (
            <>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>
                    Мини-приложение ВКонтакте создано на React + JoyUI и подключено к экосистеме VK Mini Apps.
                </Typography>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>
                    Отдельная страница «Подробнее» работает на PHP.
                </Typography>
                <Typography sx={{ color: 'var(--vkui--color_text_primary)' }}>
                    Оба интерфейса обращаются к серверу, который ежедневно формирует данные о новомучениках на текущую дату: <Link href="https://api.nmbook.ru" target="_blank">api.nmbook.ru</Link>
                </Typography>
            </>
        ),
    },
    {
        id: 'access',
        label: 'Доступность и использование данных',
        content: () => (
            <>
                <Typography sx={{ color: 'var(--vkui--color_text_primary)' }}>
                    Данные проекта размещены в открытом доступе для всех заинтересованных пользователей — исследователей, студентов, верующих и просто тех, кто интересуется историей Церкви.
                </Typography>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>При цитировании необходимо указывать источник:</Typography>
                <ul style={{ color: 'var(--vkui--color_text_primary)' }}>
                    <li>База данных «За Христа пострадавшие» (ПСТГУ)</li>
                    <li><Link href="https://pstgu.ru/baza-dannykh-za-khrista-postradavshie" target="_blank">pstgu.ru/baza-dannykh-za-khrista-postradavshie</Link></li>
                </ul>
            </>
        ),
    },
    {
        id: 'why',
        label: 'Зачем мы это делаем',
        content: () => (
            <>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>
                    Наш проект — это напоминание о людях, которые сохранили верность Христу и пожертвовали собой.
                </Typography>
                <Typography sx={{ color: 'var(--vkui--color_text_primary)' }}>
                    Мы хотим, чтобы их имена и подвиги не были забыты, а память о новомучениках стала частью духовного наследия и помогала современным людям понимать историю своей страны и Церкви.
                </Typography>
            </>
        ),
    },
    {
        id: 'who',
        label: 'Кто мы',
        content: () => (
            <Typography sx={{ color: 'var(--vkui--color_text_primary)' }}>
                Данное мини-приложение было разработано в рамках инициативной разработки студента ФИПМ ПСТГУ Дыбка Даниила Викторовича, <Link href="mailto:daniil@dybka.ru">daniil@dybka.ru</Link> в период с 1 по 17 сентября 2025 года.
            </Typography>
        ),
    },
    {
        id: 'help',
        label: 'Как помочь проекту',
        content: () => (
            <>
                <Typography sx={{ mb: 1, color: 'var(--vkui--color_text_primary)' }}>Вы можете поддержать развитие проекта:</Typography>
                <ul style={{ color: 'var(--vkui--color_text_primary)' }}>
                    <li style={{ marginBottom: '8px' }}>Участвовать как волонтёр (помощь в сборе и уточнении информации).</li>
                    <li style={{ marginBottom: '8px' }}>Делать предложения по улучшению интерфейса и работы виджета.</li>
                    <li>Распространять информацию о проекте среди тех, кому он может быть полезен.</li>
                </ul>
            </>
        ),
    },
];

export default function ModalFAQ({ open, onClose, modalSx, sheetSx }) {
    const [activeId, setActiveId] = useState(FAQ_SECTIONS[0].id);
    const activeSection = FAQ_SECTIONS.find(s => s.id === activeId);

    return (
        <Modal open={open} onClose={onClose} sx={modalSx}>
            <Sheet
                variant="outlined"
                sx={{
                    ...sheetSx,
                    backgroundColor: 'var(--vkui--color_background_content)',
                    borderColor: 'var(--vkui--color_separator_primary)',
                }}
            >
                <ModalClose
                    variant="plain"
                    onClick={onClose}
                    sx={{
                        mb: 1,
                        borderRadius: '50px',
                        color: 'var(--vkui--color_icon_primary)',
                        '&:hover': {
                            backgroundColor: 'var(--vkui--color_background_secondary)',
                            color: 'var(--vkui--color_icon_primary)',
                        },
                        '& svg': {
                            fill: 'var(--vkui--color_icon_primary)',
                        },
                    }}
                />
                <Typography level="h4" mb={2} sx={{ color: 'var(--vkui--color_text_primary)' }}>
                    Часто задаваемые вопросы
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        py: 0.5,
                        mb: 2,
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        '& > *': { flexShrink: 0 },
                    }}
                >
                    {FAQ_SECTIONS.map(section => (
                        <Chip
                            key={section.id}
                            variant={activeId === section.id ? 'solid' : 'outlined'}
                            color={activeId === section.id ? 'primary' : 'neutral'}
                            onClick={() => setActiveId(section.id)}
                            sx={{ cursor: 'pointer', borderRadius: '50px', px: 2.5, py: 1 }}
                        >
                            {section.label}
                        </Chip>
                    ))}
                </Box>
                {activeSection?.content()}
            </Sheet>
        </Modal>
    );
}
